/**
 * Player statistics extraction utilities.
 */

import type { FUSportsSquadMember } from "fansunited-data-layer";
import { getPositionGroup } from "./squad";

export type PositionGroup = "DEF" | "MID" | "FWD";

/**
 * Pick the signature player for each position using a position-specific
 * ranking metric — the stat that most defines the role:
 *   DEF → Clean Sheets Played (team CS the player was part of)
 *   MID → Goals + Assists combined
 *   FWD → Goals
 * Ties are broken by minutes played. Falls back to apps when the primary
 * metric is zero across the squad (e.g. early-season teams).
 */
export function getFeaturedPlayersByPosition(squad: FUSportsSquadMember[]): FUSportsSquadMember[] {
    const picks: FUSportsSquadMember[] = [];

    const minutes = (p: FUSportsSquadMember) => p.seasonStatistics?.minutes ?? 0;
    const apps = (p: FUSportsSquadMember) => p.seasonStatistics?.appearances ?? 0;
    const goals = (p: FUSportsSquadMember) => p.seasonStatistics?.goals ?? 0;
    const assists = (p: FUSportsSquadMember) => p.seasonStatistics?.assists ?? 0;
    const cleanSheets = (p: FUSportsSquadMember) => p.seasonStatistics?.cleansheets ?? 0;
    const gPlusA = (p: FUSportsSquadMember) => goals(p) + assists(p);

    const metrics: Record<PositionGroup, (p: FUSportsSquadMember) => number> = {
        DEF: cleanSheets,
        MID: gPlusA,
        FWD: goals,
    };

    const positionGroups: PositionGroup[] = ["DEF", "MID", "FWD"];

    for (const positionGroup of positionGroups) {
        const playersInPosition = squad.filter((p) => getPositionGroup(p.position) === positionGroup);
        if (playersInPosition.length === 0) continue;

        const primary = metrics[positionGroup];
        // If every player scores 0 on the primary metric (new season, no data),
        // fall back to minutes so we still surface someone meaningful.
        const anyPrimary = playersInPosition.some((p) => primary(p) > 0);
        const key = anyPrimary ? primary : minutes;

        const topPlayer = [...playersInPosition].sort((a, b) => {
            const diff = key(b) - key(a);
            if (diff !== 0) return diff;
            return minutes(b) - minutes(a) || apps(b) - apps(a);
        })[0];
        picks.push(topPlayer);
    }

    return picks;
}
